import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import { Points } from 'components/Points/Points';
import {
  useGetBatchResolvedCardsQuery,
  ResolvedCard,
  UnresolvedCard,
  groupCardsByCategory,
  CATEGORY_ORDER,
  calculateDeckValue,
  generateDecklistExport,
  CardTypeCategory,
  BatchResolveRequest,
} from 'store/services/cards/decklistApi';
import './Decklist.css';

export interface DecklistProps {
  title: string;
  author?: string;
  format?: string;
  mainboard: Array<{
    quantity: number;
    name: string;
    scryfallId?: string;
    setCode?: string;
  }>;
  sideboard?: Array<{
    quantity: number;
    name: string;
    scryfallId?: string;
    setCode?: string;
  }>;
  id?: string; // For anchor linking
}

interface DecklistData {
  mainboard: {
    resolved: ResolvedCard[];
    unresolved: UnresolvedCard[];
  };
  sideboard: {
    resolved: ResolvedCard[];
    unresolved: UnresolvedCard[];
  };
}

const Decklist: React.FC<DecklistProps> = ({
  title,
  author,
  format,
  mainboard,
  sideboard = [],
  id,
}) => {
  const [hoveredCard, setHoveredCard] = useState<ResolvedCard | null>(null);

  // Detect mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Memoize request payloads to ensure stable references for RTK Query caching
  const mainboardRequest: BatchResolveRequest = useMemo(
    () => ({
      cards: mainboard.map((card) => ({
        name: card.name,
        setCode: card.setCode,
        quantity: card.quantity,
      })),
      includePrice: true,
    }),
    [mainboard]
  );

  const sideboardRequest: BatchResolveRequest = useMemo(
    () => ({
      cards: (sideboard || []).map((card) => ({
        name: card.name,
        setCode: card.setCode,
        quantity: card.quantity,
      })),
      includePrice: true,
    }),
    [sideboard]
  );

  // Use RTK Query with caching - identical requests are deduplicated automatically
  const {
    data: mainboardData,
    isLoading: mainboardLoading,
    error: mainboardError,
  } = useGetBatchResolvedCardsQuery(mainboardRequest, {
    skip: mainboard.length === 0,
  });

  const {
    data: sideboardData,
    isLoading: sideboardLoading,
    error: sideboardError,
  } = useGetBatchResolvedCardsQuery(sideboardRequest, {
    skip: !sideboard || sideboard.length === 0,
  });

  // Derive loading and error state from queries
  const isLoading = mainboardLoading || sideboardLoading;
  const error =
    mainboardError || sideboardError ? 'Failed to load card data' : null;

  // Derive data when queries complete
  const data: DecklistData | null = useMemo(() => {
    if (!mainboardData) return null;

    return {
      mainboard: {
        resolved: mainboardData.resolved,
        unresolved: mainboardData.unresolved,
      },
      sideboard: sideboardData
        ? {
            resolved: sideboardData.resolved,
            unresolved: sideboardData.unresolved,
          }
        : { resolved: [], unresolved: [] },
    };
  }, [mainboardData, sideboardData]);

  // Set default hovered card when data loads
  useEffect(() => {
    if (data?.mainboard.resolved.length && !hoveredCard) {
      setHoveredCard(data.mainboard.resolved[0]);
    }
  }, [data, hoveredCard]);

  // Calculate stats
  const stats = useMemo(() => {
    if (!data) return null;

    const allResolved = [...data.mainboard.resolved, ...data.sideboard.resolved];
    const totalValueUSD = calculateDeckValue(allResolved);
    // Convert USD to points (USD × 100, no decimals)
    const totalPointValue = totalValueUSD !== null ? Math.round(totalValueUSD * 100) : null;

    const mainboardCount =
      data.mainboard.resolved.reduce((sum, card) => sum + card.quantity, 0) +
      data.mainboard.unresolved.reduce((sum, card) => sum + card.quantity, 0);

    const sideboardCount =
      data.sideboard.resolved.reduce((sum, card) => sum + card.quantity, 0) +
      data.sideboard.unresolved.reduce((sum, card) => sum + card.quantity, 0);

    return { totalPointValue, mainboardCount, sideboardCount };
  }, [data]);

  // Group mainboard cards by category
  const groupedMainboard = useMemo(() => {
    if (!data) return null;
    return groupCardsByCategory(data.mainboard.resolved);
  }, [data]);

  // Copy handler
  const handleCopy = useCallback(async () => {
    if (!data) return;

    const text = generateDecklistExport(
      data.mainboard.resolved,
      data.sideboard.resolved.length > 0 ? data.sideboard.resolved : undefined
    );

    try {
      await navigator.clipboard.writeText(text);
      toast.success('Decklist copied to clipboard!');
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Decklist copied to clipboard!');
    }
  }, [data]);

  // Card hover handler
  const handleCardHover = useCallback((card: ResolvedCard) => {
    setHoveredCard(card);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="decklist-container decklist-loading" id={id}>
        <div className="decklist-loading-spinner" />
        <span>Loading decklist...</span>
      </div>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <div className="decklist-container decklist-error" id={id}>
        <FontAwesomeIcon icon="exclamation-circle" />
        <span>{error || 'Failed to load decklist'}</span>
      </div>
    );
  }

  return (
    <div className="decklist-container" id={id}>
      {/* Header */}
      <div className="decklist-header">
        <div className="decklist-header-main">
          <div className="decklist-title-block">
            <div className="decklist-title-row">
              <h3 className="decklist-title">{title || 'Untitled Deck'}</h3>
              <span className="decklist-format-badge">{format || 'Unknown Format'}</span>
            </div>
            {author && <span className="decklist-author">by {author}</span>}
          </div>
        </div>
        <div className="decklist-header-meta">
          <span className="decklist-card-count">{stats?.mainboardCount} cards</span>
          {stats && stats.totalPointValue !== null && (
            <Points points={stats.totalPointValue} type="small" />
          )}
        </div>
        <button className="decklist-copy-btn" onClick={handleCopy} title="Copy decklist">
          <FontAwesomeIcon icon="copy" />
          Copy
        </button>
      </div>

      {/* Main content */}
      <div className="decklist-content">
        {/* Card lists */}
        <div className="decklist-lists">
          {/* Left column - Mainboard (excluding Land) */}
          <div className="decklist-column">
            {CATEGORY_ORDER.filter((cat) => cat !== 'Land').map((category) => {
              const cards = groupedMainboard?.[category];
              if (!cards || cards.length === 0) return null;

              const categoryCount = cards.reduce((sum, card) => sum + card.quantity, 0);

              return (
                <CardSection
                  key={category}
                  category={category}
                  count={categoryCount}
                  cards={cards}
                  onCardHover={handleCardHover}
                  isMobile={isMobile}
                />
              );
            })}

            {/* Unresolved mainboard cards */}
            {data.mainboard.unresolved.length > 0 && (
              <div className="decklist-section unresolved">
                <h4 className="decklist-section-title">
                  Unresolved ({data.mainboard.unresolved.length})
                </h4>
                <ul className="decklist-card-list">
                  {data.mainboard.unresolved.map((card, idx) => (
                    <li key={`unresolved-${idx}`} className="decklist-card-row unresolved">
                      <span className="decklist-card-qty">{card.quantity}</span>
                      <span className="decklist-card-name">{card.requestedName}</span>
                      <FontAwesomeIcon
                        icon="exclamation-circle"
                        className="decklist-card-error"
                        title={card.message}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right column - Land + Sideboard */}
          <div className="decklist-column decklist-right-column">
            {/* Land section */}
            {groupedMainboard?.Land && groupedMainboard.Land.length > 0 && (
              <CardSection
                key="Land"
                category="Land"
                count={groupedMainboard.Land.reduce((sum, card) => sum + card.quantity, 0)}
                cards={groupedMainboard.Land}
                onCardHover={handleCardHover}
                isMobile={isMobile}
              />
            )}

            {/* Sideboard */}
            {(data.sideboard.resolved.length > 0 || data.sideboard.unresolved.length > 0) && (
              <div className="decklist-sideboard-section">
                <h4 className="decklist-column-title">Sideboard</h4>
                <ul className="decklist-card-list">
                  {data.sideboard.resolved.map((card, idx) => (
                    <li
                      key={`sb-${card.scryfallID}-${idx}`}
                      className="decklist-card-row"
                      onMouseEnter={() => !isMobile && handleCardHover(card)}
                    >
                      <span className="decklist-card-qty">{card.quantity}</span>
                      <a
                        href={`/cards/${card.scryfallID}`}
                        className="decklist-card-name"
                        onMouseEnter={() => !isMobile && handleCardHover(card)}
                      >
                        {card.name}
                      </a>
                    </li>
                  ))}
                  {/* Unresolved sideboard cards */}
                  {data.sideboard.unresolved.map((card, idx) => (
                    <li key={`sb-unresolved-${idx}`} className="decklist-card-row unresolved">
                      <span className="decklist-card-qty">{card.quantity}</span>
                      <span className="decklist-card-name">{card.requestedName}</span>
                      <FontAwesomeIcon
                        icon="exclamation-circle"
                        className="decklist-card-error"
                        title={card.message}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Preview pane (desktop only) */}
        {!isMobile && (
          <div className="decklist-preview-pane">
            {hoveredCard ? (
              <>
                <img
                  src={hoveredCard.imageUris?.normal || AssetUrls.cards.defaultBack}
                  alt={hoveredCard.name}
                  className="decklist-preview-image"
                  onError={(e) => {
                    e.currentTarget.src = AssetUrls.cards.defaultBack;
                  }}
                />
                <div className="decklist-preview-info">
                  <a
                    href={`/cards/${hoveredCard.scryfallID}`}
                    className="decklist-preview-name"
                  >
                    {hoveredCard.name}
                  </a>
                  <div className="decklist-preview-set">
                    {hoveredCard.setName}
                  </div>
                  {hoveredCard.price?.usd !== null && hoveredCard.price?.usd !== undefined ? (
                    <div className="decklist-preview-price">
                      <Points points={Math.round(hoveredCard.price.usd * 100)} type="small" />
                    </div>
                  ) : (
                    <div className="decklist-preview-price unavailable">???</div>
                  )}
                </div>
              </>
            ) : (
              <div className="decklist-preview-empty">
                Hover over a card to see preview
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Card section component
interface CardSectionProps {
  category: CardTypeCategory;
  count: number;
  cards: ResolvedCard[];
  onCardHover: (card: ResolvedCard) => void;
  isMobile: boolean;
}

const CardSection: React.FC<CardSectionProps> = ({
  category,
  count,
  cards,
  onCardHover,
  isMobile,
}) => {
  // Format category name for display
  const categoryLabel = category === 'Other' ? 'Other' : `${category}s`;

  return (
    <div className="decklist-section">
      <h4 className="decklist-section-title">
        {categoryLabel} ({count})
      </h4>
      <ul className="decklist-card-list">
        {cards.map((card, idx) => (
          <li
            key={`${card.scryfallID}-${idx}`}
            className="decklist-card-row"
            onMouseEnter={() => !isMobile && onCardHover(card)}
          >
            <span className="decklist-card-qty">{card.quantity}</span>
            {isMobile ? (
              <a href={`/cards/${card.scryfallID}`} className="decklist-card-name">
                {card.name}
              </a>
            ) : (
              <a
                href={`/cards/${card.scryfallID}`}
                className="decklist-card-name"
                onMouseEnter={() => onCardHover(card)}
              >
                {card.name}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Decklist;
